import { useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  inviteUserResponseSchema,
  resetPasswordResponseSchema,
  roleSchema,
  userSchema,
  type InviteUserResponse,
  type Role,
  type User,
} from '@netdeck/contracts';
import { z } from 'zod';
import { apiRequest } from '../../shared/api/client';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { dateTimeFormat } from '../../shared/i18n/format';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { actionsWidth, DataTable, type Column } from '../../shared/ui/DataTable';
import { Field, FormActions, FormRow } from '../../shared/ui/Field';
import { EmptyState, ErrorState, Loading, Panel } from '../../shared/ui/Panel';
import { StatusDot } from '../../shared/ui/StatusDot';

const usersKey = ['users'] as const;
const rolesKey = ['users', 'roles'] as const;

export function UsersPage({ currentUserId }: { currentUserId: string }): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const language = currentLanguage();

  const queryClient = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  /**
   * Выданный пароль: и при приглашении, и при сбросе.
   *
   * Живёт только здесь, пока администратор не закроет: ни в кэше запросов,
   * ни в истории браузера ему делать нечего.
   */
  const [issued, setIssued] = useState<{ user: User; password: string; reset: boolean } | null>(
    null,
  );

  const users = useQuery({
    queryKey: usersKey,
    queryFn: () => apiRequest('/users', z.array(userSchema)),
  });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiRequest(`/users/${id}`, userSchema, { method: 'PATCH', body: { isActive } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/users/${id}/reset-password`, resetPasswordResponseSchema, { method: 'POST' }),
    onSuccess: (result) => {
      setIssued({ user: result.user, password: result.temporaryPassword, reset: true });
      void queryClient.invalidateQueries({ queryKey: usersKey });
    },
  });

  const columns: Column<User>[] = [
    {
      key: 'status',
      header: '',
      width: '28px',
      // Активные выше отключённых: отключённая учётная запись — исключение,
      // и собирать их вместе имеет смысл именно так.
      sortValue: (user) => user.isActive,
      render: (user) => (
        <StatusDot
          status={user.isActive ? 'up' : 'unknown'}
          label={user.isActive ? t('users.status.active') : t('users.status.disabled')}
        />
      ),
    },
    {
      key: 'name',
      header: t('users.column.name'),
      width: '1.1fr',
      sortValue: (user) => user.displayName,
      render: (user) => (
        <span className="flex flex-col">
          <span className="font-medium">{user.displayName}</span>
          {user.mustChangePassword ? (
            <span className="font-mono text-2xs text-warn">{t('users.passwordNotChanged')}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'email',
      header: t('users.column.email'),
      width: '1.3fr',
      sortValue: (user) => user.email,
      render: (user) => <span className="truncate font-mono text-muted">{user.email}</span>,
    },
    {
      key: 'roles',
      header: t('users.column.roles'),
      width: '1fr',
      sortValue: (user) => user.roles.map((role) => role.name).join(', '),
      render: (user) => (
        <span className="truncate text-muted">
          {user.roles.map((role) => role.name).join(', ') || t('common.dash')}
        </span>
      ),
    },
    {
      key: 'lastLogin',
      header: t('users.column.lastLogin'),
      width: '150px',
      // Ни разу не входившие уходят вниз: `null` считается отсутствием
      // значения, а не самой ранней датой.
      sortValue: (user) => user.lastLoginAt,
      render: (user) => (
        <span className="font-mono text-xs text-dim">
          {user.lastLoginAt
            ? dateTimeFormat(language, {
                hour12: false,
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }).format(new Date(user.lastLoginAt))
            : t('users.neverLoggedIn')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      // Правка, сброс пароля и включение — три кнопки. Было 104 px, то есть
      // на двенадцать у́же ряда: он вылезал из ячейки и тянул за собой
      // горизонтальную прокрутку всей страницы.
      width: actionsWidth(3),
      align: 'right',
      /*
        Действия — иконками без подписей. Три подписанные кнопки в строке
        распирали таблицу шире экрана, а `Button` прямо допускает кнопку
        без подписи «в плотном ряду, где на текст нет места», требуя взамен
        `aria-label`. Смысл при этом не теряется: у каждой кнопки есть
        и подсказка при наведении, и имя для скринридера.
      */
      render: (user) => (
        <span className="inline-flex items-center justify-end gap-1">
          <Button
            icon="pencil"
            size="sm"
            variant="ghost"
            aria-label={t('users.editAria', { name: user.displayName })}
            title={t('users.editTitle')}
            onClick={() => {
              setInviting(false);
              setEditing(user);
            }}
          />

          {user.id === currentUserId ? (
            // Свой пароль меняют сменой пароля: сброс оборвал бы текущую
            // сессию. Отключить себя нельзя вовсе — можно остаться
            // единственным администратором и потерять доступ.
            <span className="px-1 font-mono text-2xs text-faint">{t('users.itIsYou')}</span>
          ) : (
            <>
              <Button
                icon="key"
                size="sm"
                variant="ghost"
                disabled={resetPassword.isPending}
                aria-label={t('users.resetAria', { name: user.displayName })}
                title={t('users.resetTitle')}
                onClick={() => {
                  if (confirm(t('users.resetConfirm', { name: user.displayName }))) {
                    resetPassword.mutate(user.id);
                  }
                }}
              />

              <Button
                icon="power"
                size="sm"
                variant={user.isActive ? 'danger' : 'ghost'}
                disabled={setActive.isPending}
                aria-label={
                  user.isActive
                    ? t('users.disableAria', { name: user.displayName })
                    : t('users.enableAria', { name: user.displayName })
                }
                title={user.isActive ? t('users.disableTitle') : t('users.enableTitle')}
                onClick={() => setActive.mutate({ id: user.id, isActive: !user.isActive })}
              />
            </>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
      {issued ? <TemporaryPassword issued={issued} onClose={() => setIssued(null)} /> : null}

      {resetPassword.error ? (
        <ErrorState title={t('users.resetFailed')} hint={errorText(resetPassword.error)} />
      ) : null}

      <Panel
        title={t('nav.users')}
        subtitle={users.data ? `${users.data.length}` : undefined}
        actions={
          <Button
            icon={inviting ? 'close' : 'userPlus'}
            size="sm"
            onClick={() => {
              setEditing(null);
              setInviting((value) => !value);
            }}
          >
            {inviting ? t('common.action.cancel') : t('users.invite')}
          </Button>
        }
      >
        {inviting ? (
          <InviteForm
            onInvited={(result) => {
              setInviting(false);
              setIssued({ user: result.user, password: result.temporaryPassword, reset: false });
              void queryClient.invalidateQueries({ queryKey: usersKey });
            }}
          />
        ) : null}

        {editing ? (
          <EditUserForm
            key={editing.id}
            user={editing}
            onDone={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              void queryClient.invalidateQueries({ queryKey: usersKey });
            }}
          />
        ) : null}

        {users.isPending ? (
          <Loading />
        ) : users.error ? (
          <ErrorState
            title={t('users.loadFailed')}
            hint={t('users.loadFailedHint', { reason: errorText(users.error) })}
          />
        ) : (
          <DataTable
            columns={columns}
            rows={users.data}
            rowKey={(user) => user.id}
            rowDanger={(user) => !user.isActive}
            empty={<EmptyState title={t('users.emptyTitle')} hint={t('users.emptyHint')} />}
          />
        )}
      </Panel>
    </div>
  );
}

/**
 * Временный пароль показывается один раз (§11, фаза 1, задача 4).
 *
 * Один блок и для приглашения, и для сброса: пароль в обоих случаях
 * придумал сервер, живёт до закрытия блока и на сервере в открытом виде
 * не хранится. Нигде больше он не сохраняется — ни в состоянии запросов,
 * ни в истории.
 */
function TemporaryPassword({
  issued,
  onClose,
}: {
  issued: { user: User; password: string; reset: boolean };
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-warn/40 bg-overlay px-4 py-3">
      <div className="text-base font-medium">
        {issued.reset
          ? t('users.passwordReset', { name: issued.user.displayName })
          : t('users.userCreated', { name: issued.user.displayName })}
      </div>
      <div className="text-sm text-muted">
        {t('users.temporaryPasswordNote')}
        {issued.reset ? ` ${t('users.oldPasswordDead')}` : ''}
      </div>
      <div className="flex items-center gap-3 pt-1">
        <code className="rounded-sm border border-line-strong bg-raised px-3 py-2 font-mono text-lg tracking-wide select-all">
          {issued.password}
        </code>
        <Button
          icon="copy"
          size="sm"
          variant="ghost"
          onClick={() => void navigator.clipboard?.writeText(issued.password)}
        >
          {t('common.action.copy')}
        </Button>
        <Button icon="check" size="sm" onClick={onClose}>
          {t('users.written')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Правка пользователя: имя и роли.
 *
 * Роли отмечаются флажками, а не выбираются одним списком: §10.3 задаёт
 * итоговые права как объединение ролевых, и человек может быть сразу
 * инженером и наблюдателем. Выпадающий список с одним значением этого
 * не выражает — а именно он стоит в форме приглашения, где хватает одной
 * роли на старте.
 *
 * Без ролей пользователя оставить нельзя: учётная запись без единого права
 * не отличается от отключённой, но выглядит рабочей.
 */
function EditUserForm({
  user,
  onDone,
  onSaved,
}: {
  user: User;
  onDone: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [displayName, setDisplayName] = useState(user.displayName);
  const [roleIds, setRoleIds] = useState<string[]>(user.roles.map((role) => role.id));

  const roles = useQuery({
    queryKey: rolesKey,
    queryFn: () => apiRequest('/users/roles', z.array(roleSchema)),
  });

  const save = useMutation({
    mutationFn: (input: { displayName?: string; roleIds?: string[] }) =>
      apiRequest(`/users/${user.id}`, userSchema, { method: 'PATCH', body: input }),
    onSuccess: onSaved,
  });

  const rolesChanged =
    roleIds.length !== user.roles.length ||
    roleIds.some((id) => !user.roles.some((role) => role.id === id));
  const nameChanged = displayName.trim() !== user.displayName;

  function submit(event: FormEvent): void {
    event.preventDefault();

    save.mutate({
      ...(nameChanged ? { displayName: displayName.trim() } : {}),
      ...(rolesChanged ? { roleIds } : {}),
    });
  }

  return (
    <FormRow onSubmit={submit}>
      <div className="w-full sm:w-[220px]">
        <Field
          label={t('users.form.name')}
          required
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-sm text-muted">{t('users.column.roles')}</span>
        <div className="flex flex-wrap items-center gap-3 pt-1.5">
          {(roles.data ?? []).map((role: Role) => (
            <label key={role.id} className="flex items-center gap-1.5 text-base">
              <input
                type="checkbox"
                checked={roleIds.includes(role.id)}
                onChange={(event) =>
                  setRoleIds((current) =>
                    event.target.checked
                      ? [...current, role.id]
                      : current.filter((id) => id !== role.id),
                  )
                }
              />
              {role.name}
              <span className="font-mono text-2xs text-dim">
                {t('common.count.permissions', { count: role.permissions.length })}
              </span>
            </label>
          ))}
        </div>
      </div>

      <FormActions>
        <Button
          icon="check"
          type="submit"
          variant="primary"
          size="sm"
          disabled={
            save.isPending ||
            roleIds.length === 0 ||
            !displayName.trim() ||
            (!nameChanged && !rolesChanged)
          }
        >
          {save.isPending ? t('sites.form.saving') : t('common.action.save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t('common.action.cancel')}
        </Button>

        {roleIds.length === 0 ? (
          <span className="text-sm text-muted">{t('users.form.roleRequired')}</span>
        ) : null}
        {save.error ? <span className="text-sm text-down-fg">{errorText(save.error)}</span> : null}
      </FormActions>
    </FormRow>
  );
}

function InviteForm({
  onInvited,
}: {
  onInvited: (result: InviteUserResponse) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleId, setRoleId] = useState('');

  const roles = useQuery({
    queryKey: rolesKey,
    queryFn: () => apiRequest('/users/roles', z.array(roleSchema)),
  });

  const invite = useMutation({
    mutationFn: (input: { email: string; displayName: string; roleIds: string[] }) =>
      apiRequest('/users', inviteUserResponseSchema, { method: 'POST', body: input }),
    onSuccess: onInvited,
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    invite.mutate({ email, displayName, roleIds: [roleId] });
  }

  return (
    <FormRow onSubmit={submit}>
      <div className="w-full sm:w-[260px]">
        <Field
          label={t('auth.login.email')}
          type="email"
          required
          mono
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="engineer@example.org"
          hint={t('users.form.emailHint')}
        />
      </div>
      <div className="w-full sm:w-[220px]">
        <Field
          label={t('users.form.name')}
          required
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder={t('users.form.namePlaceholder')}
        />
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-sm text-muted">{t('users.form.role')}</span>
        <select
          required
          value={roleId}
          onChange={(event) => setRoleId(event.target.value)}
          className="h-[34px] rounded-sm border border-line-strong bg-raised px-2 text-base text-fg"
        >
          <option value="">{t('agents.form.pickSite')}</option>
          {(roles.data ?? []).map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
      </label>

      <FormActions>
        <Button
          icon="userPlus"
          type="submit"
          variant="primary"
          size="sm"
          disabled={invite.isPending || !email || !displayName || !roleId}
        >
          {invite.isPending ? t('sites.form.creating') : t('users.invite')}
        </Button>

        {invite.error ? (
          <span className="text-sm text-down-fg">{errorText(invite.error)}</span>
        ) : null}
      </FormActions>
    </FormRow>
  );
}
